// Aggregator (Swiggy / Zomato) settings — local-first config backing the
// Swiggy & Zomato screen. The Android app integrates with the partner APIs
// server-side; here we keep per-platform commission / store-id / packaging /
// menu-sync state locally so the screen can reconcile online-order earnings
// (gross → commission → net payout) without a backend.

import { OnlineOrderChannel } from '../types';
import { cachedKey } from './idb';

export type AggregatorPlatform = 'Swiggy' | 'Zomato';

export interface PlatformConfig {
  enabled: boolean;
  storeId: string;            // partner outlet / store id
  commissionPercent: number;  // aggregator commission on gross (%)
  packagingCharge: number;    // default packaging charge added per order
  gstOnCommission: number;    // GST charged on the commission (%) — usually 18
  menuSyncedCount: number;    // items last pushed to the platform menu
  menuSyncedAt?: string;      // ISO of last menu sync
  supportNo: string;
}

export interface AggregatorSettings {
  Swiggy: PlatformConfig;
  Zomato: PlatformConfig;
}

export const PLATFORMS: AggregatorPlatform[] = ['Swiggy', 'Zomato'];

// Channels that count as a given aggregator platform on online orders.
export const platformOf = (c: OnlineOrderChannel): AggregatorPlatform | null =>
  c === 'Swiggy' ? 'Swiggy' : c === 'Zomato' ? 'Zomato' : null;

const defaultPlatform = (p: AggregatorPlatform): PlatformConfig => ({
  enabled: true,
  storeId: '',
  commissionPercent: p === 'Swiggy' ? 22 : 21, // typical aggregator take rate
  packagingCharge: 0,
  gstOnCommission: 18,
  menuSyncedCount: 0,
  supportNo: p === 'Swiggy' ? '080-67466777' : '080-46708888',
});

export const DEFAULT_AGGREGATOR_SETTINGS: AggregatorSettings = {
  Swiggy: defaultPlatform('Swiggy'),
  Zomato: defaultPlatform('Zomato'),
};

const aggregatorCache = cachedKey<Partial<AggregatorSettings>>('sconnect.aggregatorSettings', {});

export function getAggregatorSettings(): AggregatorSettings {
  const parsed = aggregatorCache.get();
  // merge so newly-added fields fall back to defaults
  return {
    Swiggy: { ...DEFAULT_AGGREGATOR_SETTINGS.Swiggy, ...(parsed.Swiggy ?? {}) },
    Zomato: { ...DEFAULT_AGGREGATOR_SETTINGS.Zomato, ...(parsed.Zomato ?? {}) },
  };
}

export function saveAggregatorSettings(s: AggregatorSettings): void {
  aggregatorCache.set(s);
}

// Per-platform payout breakdown for a set of orders' gross sales.
export interface PayoutBreakup {
  orders: number;
  delivered: number;
  cancelled: number;
  gross: number;
  commission: number;
  gstOnCommission: number;
  netPayout: number;
  avgOrderValue: number;
}

export function computePayout(gross: number, deliveredCount: number, cancelled: number, orders: number, cfg: PlatformConfig): PayoutBreakup {
  const commission = gross * (cfg.commissionPercent / 100);
  const gstOnComm = commission * (cfg.gstOnCommission / 100);
  const netPayout = gross - commission - gstOnComm;
  return {
    orders,
    delivered: deliveredCount,
    cancelled,
    gross,
    commission,
    gstOnCommission: gstOnComm,
    netPayout,
    avgOrderValue: orders > 0 ? gross / orders : 0,
  };
}
